/**
 * SceneManager — Controls active game scene.
 * Scenes are lazy-imported on demand to minimize initial bundle size.
 *
 * @module SceneManager
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from './EventBus.js';

// ── Fade overlay ──────────────────────────────────────────────────────────────
// A single full-screen div used for cross-scene fade transitions.
// Created once on first use and reused for every transition.

function _getFadeOverlay() {
  let el = document.getElementById('scene-fade-overlay');
  if (!el) {
    el = document.createElement('div');
    el.id = 'scene-fade-overlay';
    Object.assign(el.style, {
      position: 'fixed',
      inset: '0',
      background: '#0a0808',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '450',
      transition: 'opacity 0.25s ease',
    });
    const mount = document.getElementById('ui-overlay') ?? document.body;
    mount.appendChild(el);
  }
  return el;
}

function _fadeOut(ms = 250) {
  return new Promise(resolve => {
    const el = _getFadeOverlay();
    el.style.pointerEvents = 'all';
    el.style.transition = `opacity ${ms}ms ease`;
    el.style.opacity = '1';
    setTimeout(resolve, ms);
  });
}

function _fadeIn(ms = 250) {
  return new Promise(resolve => {
    const el = _getFadeOverlay();
    el.style.transition = `opacity ${ms}ms ease`;
    el.style.opacity = '0';
    setTimeout(() => {
      el.style.pointerEvents = 'none';
      resolve();
    }, ms);
  });
}

/**
 * Map of scene names to their dynamic import functions.
 * Add new scenes here as they are created.
 */
const SCENE_LOADERS = {
  boot:        () => import('../scenes/BootScene.js'),
  mainMenu:    () => import('../scenes/MainMenuScene.js'),
  worldMap:    () => import('../scenes/WorldMapScene.js'),
  regionMap:   () => import('../scenes/RegionMapScene.js'),
  localMap:    () => import('../scenes/LocalMapScene.js'),
  combat:      () => import('../scenes/CombatScene.js'),
  charCreate:  () => import('../scenes/CharCreateScene.js'),
  partyBuilder:() => import('../scenes/PartyBuilderScene.js'),
  town:        () => import('../scenes/TownScene.js'),
  dungeon:     () => import('../scenes/DungeonScene.js').then(m => ({ default: m.default })),
};

const SceneManager = {
  /** @type {object|null} Current active scene instance */
  _current: null,

  /** @type {string|null} Name of current scene */
  _currentName: null,

  /** @type {object|null} Renderer reference */
  _renderer: null,

  /** Public accessor for the renderer instance (used by scenes) */
  get renderer() { return this._renderer; },

  /** @type {boolean} Whether a transition is already in progress */
  _transitioning: false,

  /**
   * Initialize the SceneManager with a renderer reference.
   * @param {object} renderer — Renderer instance from Renderer.js
   */
  init(renderer) {
    this._renderer = renderer;
    DebugLog.info('[boot] SceneManager initialized');
  },

  /**
   * Transition to a new scene.
   * Destroys the current scene (if any), then instantiates and initializes the new one.
   *
   * @param {string} sceneName — One of the keys in SCENE_LOADERS
   * @param {object} [data={}] — Optional data passed to scene.init(data)
   * @returns {Promise<void>}
   */
  async transition(sceneName, data = {}) {
    if (this._transitioning) {
      DebugLog.warn(`[boot] SceneManager: transition to "${sceneName}" requested while already transitioning — ignoring`);
      return;
    }

    this._transitioning = true;
    const from = this._currentName;

    DebugLog.info(`[boot] Scene transition: ${from ?? 'none'} → ${sceneName}`);

    // Skip fade for the initial boot scene (nothing to fade from)
    const skipFade = (from === null || from === 'boot');
    if (!skipFade) await _fadeOut(200);

    // Destroy existing scene
    if (this._current) {
      try {
        if (typeof this._current.destroy === 'function') {
          await this._current.destroy();
        }
      } catch (err) {
        DebugLog.error(`[boot] Error destroying scene "${from}": ${err.message}`);
      }
      this._current = null;
    }

    // Load new scene module
    const loader = SCENE_LOADERS[sceneName];
    if (!loader) {
      DebugLog.error(`[boot] Unknown scene: "${sceneName}"`);
      this._transitioning = false;
      return;
    }

    let SceneClass;
    try {
      const module = await loader();
      // Support both default export class and named export
      SceneClass = module.default ?? module[Object.keys(module)[0]];
    } catch (err) {
      DebugLog.error(`[boot] Failed to load scene module "${sceneName}": ${err.message}`);
      this._transitioning = false;
      return;
    }

    // Instantiate and init
    try {
      this._current = new SceneClass();
      this._currentName = sceneName;

      if (typeof this._current.init === 'function') {
        await this._current.init({ renderer: this._renderer, ...data });
      }
    } catch (err) {
      DebugLog.error(`[boot] Failed to initialize scene "${sceneName}": ${err.message}`);
      this._transitioning = false;
      return;
    }

    EventBus.emit('scene:change', { from, to: sceneName });

    // Fade in after scene is ready
    if (!skipFade) await _fadeIn(200);

    this._transitioning = false;

    DebugLog.info(`[boot] Scene ready: ${sceneName}`);
  },

  /**
   * Return the currently active scene instance.
   * @returns {object|null}
   */
  getCurrent() {
    return this._current;
  },

  /**
   * Return the name of the currently active scene.
   * @returns {string|null}
   */
  getCurrentName() {
    return this._currentName;
  },

  /**
   * Call update() on the current scene if it has one.
   * Typically called from the game loop (Renderer tick).
   * @param {number} deltaTime — Milliseconds since last frame
   */
  update(deltaTime) {
    if (this._current && typeof this._current.update === 'function') {
      this._current.update(deltaTime);
    }
  },
};

export default SceneManager;
